import { useEffect, useState } from "react";
// Exact conversions from the ENTERED value only — never chain conversions (no rounding drift).
export const IN_TO_CM = 2.54;
export const LB_TO_KG = 0.45359237;
export const KEYS = ["height", "weight", "chest", "waist", "hip", "inseam", "foot_length"];
export const LABEL = { height: "Height", weight: "Weight", chest: "Chest", waist: "Waist", hip: "Hip", inseam: "Inseam", foot_length: "Foot length", sleeve: "Sleeve" };
export const isWeight = (k) => k === "weight";
export const unitFor = (k, system) => (isWeight(k) ? (system === "metric" ? "kg" : "lb") : (system === "metric" ? "cm" : "in"));

export function convert(value, from, to) {
  const v = Number(value);
  if (!Number.isFinite(v) || from === to) return v;
  if (from === "in" && to === "cm") return v * IN_TO_CM;
  if (from === "cm" && to === "in") return v / IN_TO_CM;
  if (from === "lb" && to === "kg") return v * LB_TO_KG;
  if (from === "kg" && to === "lb") return v / LB_TO_KG;
  return v;
}

export const fmt = (v) => (Number.isFinite(Number(v)) ? String(Math.round(Number(v) * 10) / 10) : "—");

// Display a stored entry {value, unit} in the requested system; the stored value is the source of truth.
export function display(entry, system) {
  if (!entry || entry.value === "" || entry.value === null || entry.value === undefined) return "";
  const to = unitFor(entry.key, system);
  return fmt(convert(entry.value, entry.unit, to));
}

export const ORIGIN_LABEL = { measured: "Measured", parent_estimate: "Estimate", ai_estimate: "Estimate", unknown: "Unconfirmed" };

// ---- shared unit preference (persisted; one value for tiles, editor, size guide, compare) ----
const UNITS_KEY = "siblingo.units";
export function readUnits() {
  try { const v = localStorage.getItem(UNITS_KEY); return v === "metric" ? "metric" : "imperial"; } catch { return "imperial"; }
}
export function useUnits() {
  const [system, set] = useState(readUnits);
  useEffect(() => {
    const on = () => set(readUnits());
    window.addEventListener("siblingo-units", on);
    window.addEventListener("storage", on);
    return () => { window.removeEventListener("siblingo-units", on); window.removeEventListener("storage", on); };
  }, []);
  const setSystem = (v) => { try { localStorage.setItem(UNITS_KEY, v); } catch {} set(v); window.dispatchEvent(new Event("siblingo-units")); };
  return [system, setSystem];
}

// Normalised (cm / kg) → preferred display unit, one rounding.
const NORM_FACTOR = { in: IN_TO_CM, cm: 1, mm: 0.1, lb: LB_TO_KG, kg: 1 };
export const fromNorm = (key, norm, system) => (norm === null || norm === undefined ? null : Math.round((norm / NORM_FACTOR[unitFor(key, system)]) * 10) / 10);

// A published chart cell {bound_kind, lower, upper, point, unit} rendered in the preferred system.
// Native unit → shown as published; other unit → converted from the published numbers (never re-converted), flagged.
export function cellText(m, system) {
  const to = unitFor(m.key, system);
  const from = (m.unit || "").toLowerCase();
  const native = from === to;
  const c = (v) => (v === null || v === undefined ? null : native ? Number(v) : Math.round(convert(v, from, to) * 10) / 10);
  const g = (v) => (v === null ? "?" : String(v));
  let text = "";
  if (m.bound_kind === "range") text = `${g(c(m.lower))}–${g(c(m.upper))} ${to}`;
  else if (m.bound_kind === "upper_only") text = `up to ${g(c(m.upper))} ${to}`;
  else if (m.bound_kind === "lower_only") text = `${g(c(m.lower))} ${to} and up`;
  else if (m.point !== null && m.point !== undefined) text = `${g(c(m.point))} ${to}`;
  else text = m.text || "";
  return { text, converted: !native && !!text, source_text: m.text };
}
